import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const SECTIONS = [
  {
    no: "01",
    title: "Geltungsbereich",
    body: [
      "Diese Allgemeinen Geschäftsbedingungen gelten für sämtliche Angebote, Lieferungen und Dienstleistungen der EMMAX Dienstleistungs GmbH gegenüber Unternehmen, Gewerbetreibenden und öffentlichen Auftraggebern.",
    ],
  },
  {
    no: "02",
    title: "Vertragsabschluss",
    body: [
      "Ein Vertrag kommt durch die schriftliche Auftragsbestätigung oder durch die Ausführung der beauftragten Leistungen zustande.",
    ],
  },
  {
    no: "03",
    title: "Leistungsumfang",
    body: [
      "Der Umfang der Leistungen ergibt sich aus dem jeweiligen Angebot, Vertrag oder Leistungsverzeichnis.",
      "Zusätzliche Leistungen, die vom ursprünglichen Auftrag abweichen, werden gesondert vergütet.",
    ],
  },
  {
    no: "04",
    title: "Preise",
    body: [
      "Es gelten die im Angebot vereinbarten Preise.",
      "Alle Preise verstehen sich, sofern nicht anders angegeben, exklusive der gesetzlichen Mehrwertsteuer.",
    ],
  },
  {
    no: "05",
    title: "Zahlungsbedingungen",
    body: [
      "Rechnungen sind innerhalb von 30 Tagen ohne Abzug zahlbar, sofern keine andere Vereinbarung getroffen wurde.",
      "Bei Zahlungsverzug behalten wir uns vor, Verzugszinsen sowie Mahnkosten gemäss den gesetzlichen Bestimmungen zu berechnen.",
    ],
  },
  {
    no: "06",
    title: "Termine",
    body: [
      "Vereinbarte Termine werden nach bestem Wissen eingehalten.",
      "Verzögerungen aufgrund höherer Gewalt, behördlicher Anordnungen, Materialengpässen oder sonstiger unvorhersehbarer Ereignisse verlängern vereinbarte Fristen entsprechend.",
    ],
  },
  {
    no: "07",
    title: "Mitwirkungspflichten des Auftraggebers",
    body: [
      "Der Auftraggeber stellt sicher, dass sämtliche für die Leistungserbringung erforderlichen Informationen, Zugänge sowie Arbeitsbereiche rechtzeitig zur Verfügung stehen.",
    ],
  },
  {
    no: "08",
    title: "Abnahme",
    body: [
      "Die Leistung gilt als abgenommen, sofern der Auftraggeber nicht innerhalb von fünf Werktagen nach Abschluss der Arbeiten wesentliche Mängel schriftlich mitteilt.",
    ],
  },
  {
    no: "09",
    title: "Gewährleistung",
    body: [
      "Offensichtliche Mängel sind unverzüglich schriftlich anzuzeigen.",
      "Der EMMAX Dienstleistungs GmbH ist zunächst Gelegenheit zur Nachbesserung einzuräumen.",
    ],
  },
  {
    no: "10",
    title: "Haftung",
    body: [
      "Die Haftung der EMMAX Dienstleistungs GmbH beschränkt sich auf Vorsatz und grobe Fahrlässigkeit, soweit gesetzlich zulässig.",
      "Eine Haftung für indirekte Schäden, Folgeschäden oder entgangenen Gewinn ist ausgeschlossen.",
    ],
  },
  {
    no: "11",
    title: "Vertraulichkeit",
    body: [
      "Beide Vertragsparteien verpflichten sich, sämtliche im Rahmen der Zusammenarbeit bekannt gewordenen vertraulichen Informationen vertraulich zu behandeln.",
    ],
  },
  {
    no: "12",
    title: "Datenschutz",
    body: [
      "Die Verarbeitung personenbezogener Daten erfolgt gemäss der Datenschutzerklärung der EMMAX Dienstleistungs GmbH.",
    ],
  },
  {
    no: "13",
    title: "Gerichtsstand und anwendbares Recht",
    body: [
      "Es gilt ausschliesslich schweizerisches Recht.",
      "Gerichtsstand ist – soweit gesetzlich zulässig – der Sitz der EMMAX Dienstleistungs GmbH.",
    ],
  },
  {
    no: "14",
    title: "Salvatorische Klausel",
    body: [
      "Sollten einzelne Bestimmungen dieser AGB ganz oder teilweise unwirksam sein oder werden, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.",
    ],
  },
];

export default function AGB({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="agb-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[60] flex justify-end bg-[#0d0d0f]/80 backdrop-blur-sm"
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative h-full w-full max-w-2xl overflow-y-auto border-l border-white/10 bg-[#111214] px-8 py-16 lg:px-14"
          >
            <button
              data-testid="agb-close"
              onClick={onClose}
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-sm border border-white/10 text-white transition-colors duration-300 hover:border-[#4fd1c5] hover:text-[#4fd1c5]"
              aria-label="Schliessen"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>

            <span className="font-mono-spc text-[11px] uppercase tracking-[0.35em] text-[#4fd1c5]">Rechtliches</span>
            <h2 className="mb-6 mt-4 font-head text-4xl font-light uppercase tracking-tighter text-metal sm:text-5xl">
              Allgemeine Geschäftsbedingungen
            </h2>
            <p className="mb-10 font-light leading-relaxed text-[#c0c5ce]/70">
              Swiss Precision Cleaning ist eine Marke der EMMAX Dienstleistungs GmbH. Vertragspartner sämtlicher
              Leistungen ist die EMMAX Dienstleistungs GmbH, Waldeckstrasse 5, 4053 Basel, Schweiz.
            </p>

            <div className="flex flex-col">
              {SECTIONS.map((s) => (
                <div key={s.no} className="grid grid-cols-[auto_1fr] gap-5 border-t border-white/10 py-7 first:border-t-0 sm:gap-8">
                  <span className="font-head text-3xl font-extralight text-[#4fd1c5]/60">{s.no}</span>
                  <div>
                    <h3 className="mb-3 font-head text-lg font-medium tracking-tight text-white">{s.title}</h3>
                    <div className="space-y-2 font-light leading-relaxed text-[#c0c5ce]/80">
                      {s.body.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
